import type { PriceComponent } from '../../api/objects'
import { t } from '../../i18n'
import { money, months } from '../../format'
import { priceComponentLabel } from '../../labels'
import { breakdown } from './price'
import type { PriceRow } from './price'

/**
 * What the offer costs, one component at a time.
 *
 * Never a single total. Every line carries its own known / estimated / unknown
 * kind, and a total assembled from an unknown part is unknown rather than the
 * sum of the parts we happen to know (invariant 2). Even when every part is
 * known this screen does not add them up: a monthly figure only means something
 * with its scope attached — bills in or out, whole unit or per person — and that
 * is a decision the domain makes with a client's own terms in hand, not
 * arithmetic a card can do on its own.
 */
export function PriceTable({ components }: { readonly components: readonly PriceComponent[] }) {
  const price = breakdown(components)

  return (
    <div className="price">
      {/*
        Nothing recorded at all still shows the required rows below, each marked
        as never asked. Saying it twice is deliberate: an empty price is a listing
        nobody has worked on, and the rows say which questions are outstanding.
      */}
      {components.length === 0 ? (
        <p className="panel-block__empty">{t('listing.priceEmpty')}</p>
      ) : null}

      <Group titleKey="listing.monthly" rows={price.monthly} />
      <Group titleKey="listing.upfront" rows={price.upfront} />

      {/*
        Said out loud rather than left to be noticed. A manager who reads five
        known lines and quotes their sum has quoted a number nobody checked, and
        every part of this card exists to stop that one sentence.
      */}
      {!price.monthlyCanBeStated && price.monthly.length > 0 ? (
        <p className="price__verdict">
          <strong>
            {t('listing.monthlyNotStated', {
              components: [...price.notStated, ...price.neverAsked]
                .filter((name, index, all) => all.indexOf(name) === index)
                .map(priceComponentLabel)
                .join(', '),
            })}
          </strong>
          <span className="price__verdict-hint">{t('listing.monthlyNotStatedHint')}</span>
        </p>
      ) : null}
    </div>
  )
}

function Group({
  titleKey,
  rows,
}: {
  readonly titleKey: 'listing.monthly' | 'listing.upfront'
  readonly rows: readonly PriceRow[]
}) {
  if (rows.length === 0) return null

  return (
    <div className="price__group">
      <h4 className="price__group-title">{t(titleKey)}</h4>
      <ul className="price__rows">
        {rows.map((row) => (
          <Row key={row.key} row={row} />
        ))}
      </ul>
    </div>
  )
}

function Row({ row }: { readonly row: PriceRow }) {
  const stated = months(row.monthsHundredths)

  return (
    <li className={`price-row price-row--${row.state}`}>
      <span className="price-row__name">{priceComponentLabel(row.component)}</span>

      <span className="price-row__value">
        {row.state === 'missing' ? (
          // Not an amount at all. A blank cell here reads as a zero, and a zero
          // here is a client quoted a rent with no bills in it.
          <span className="price-row__missing">{t('listing.neverAsked')}</span>
        ) : row.state === 'unknown' ? (
          <span className="price-row__unknown">{money(row.amount)}</span>
        ) : (
          money(row.amount)
        )}
        {/*
          "Two months' deposit" as it was actually said. The figure follows from a
          rent that changes and the statement does not, so both are kept.
        */}
        {stated === undefined ? null : <span className="price-row__months">{stated}</span>}
      </span>

      <span className="price-row__note">
        {row.state === 'missing'
          ? t('listing.neverAskedHint')
          : row.state === 'unknown'
            ? t('listing.unknownHint')
            : row.note}
      </span>
    </li>
  )
}
